<template>
  <div class="card__wrapper">
    <div v-if="order">
      <div class="card">
        <h1 class="card__title">{{ $t('messages.order') }} #{{ $route.params.id }}</h1>
        <div class="card__description">
          <div>
            <div class="d-flex-responsive-order">
              <div class="d-flex-responsive-order width-50 details-margin align-items-center">
                <div class="mr-3">
                  <span class="font-weight-bold">{{ $t('messages.date') }}</span>: {{ formatDate(order.created_date) }}
                </div>
                <div class="d-flex align-items-center">
                  <span class="font-weight-bold">{{ $t('messages.status') }}</span>:
                  <div v-if="!isAdmin" class="ml-1">{{ $t('statuses.' + order.status) }}</div>
                  <div v-if="isAdmin" class="ml-1">
                    <b-form-select v-model="status">
                      <option v-for="status in statuses" :key="'s-' + status" :value="status">{{ $t('statuses.' + status) }}</option>
                    </b-form-select>
                  </div>
                </div>
              </div>
              <div class="d-flex width-50 justify-content-end mb-3">
                <div class="mr-3 d-flex align-items-center">
                  <font-awesome-icon icon="check-circle" class="green mr-2" size="2x"></font-awesome-icon>
                  {{ $t('messages.delivered') }}
                </div>
                <div class="mr-3 d-flex align-items-center">
                  <font-awesome-icon icon="check-circle" class="yellow mr-2" size="2x"></font-awesome-icon>
                  {{ $t('messages.delivered_partly') }}
                </div>
                <div class="mr-1 d-flex align-items-center">
                  <font-awesome-icon icon="times-circle" class="red mr-2" size="2x"></font-awesome-icon>
                  {{ $t('messages.delivered_not_yet') }}
                </div>
              </div>
            </div>
            <b-table striped bordered stacked="md" :items="order.products" :fields="fields">
              <template v-slot:cell(image)="row">
                <b-img :src="row.item.image" fluid :alt="row.item.name" @click="navigateToProduct(row.item)" class="pointer" />
              </template>

              <template v-slot:cell(price)="row">
                € {{ row.item.price | round(2) }}
              </template>

              <template v-slot:cell(total)="row">
                € {{ (row.item.amount * row.item.price) | round(2) }}
              </template>

              <template v-slot:cell(delivered)="row">
                <div class="d-flex align-items-center">
                  <div>
                    <font-awesome-icon icon="check-circle" class="green" v-if="row.item.delivered === row.item.amount" size="2x"></font-awesome-icon>
                    <font-awesome-icon icon="check-circle" class="yellow" v-if="row.item.delivered > 0 && row.item.delivered < row.item.amount" size="2x"></font-awesome-icon>
                    <font-awesome-icon icon="times-circle" class="red" v-if="row.item.delivered === 0" size="2x"></font-awesome-icon>
                  </div>
                  <div v-if="isAdmin" class="ml-1">
                    <b-form-select v-model="row.item.delivered" @change="changeDelivered(row.item)">
                      <option :value="0">0</option>
                      <option v-for="amount in row.item.reserved" :key="'d-' + amount" :value="amount">{{ amount }}</option>
                    </b-form-select>
                  </div>
                </div>
              </template>
            </b-table>
            <div class="d-flex">
              <div class="d-flex flex-column w-100">
                <div class="d-flex flex-column w-100 mb-3" v-if="order.comments || isAdmin">
                  <p class="font-weight-bold">{{ $t('messages.comments') }}</p>
                  <div class="d-flex">
                    <nl2br tag="p" :text="order.comments" v-if="!isAdmin" />
                    <b-form @submit.prevent="saveComment" v-if="isAdmin" class="w-100">
                      <b-form-textarea id="comments" name="comments" rows="3"
                                       v-model.trim="order.comments" class="mb-2"></b-form-textarea>
                      <notification-button :message="$t('messages.save')" :notification="notifications.saveComment" field="saveComment" class="float-right"></notification-button>
                    </b-form>
                  </div>
                </div>
                <div class="d-flex flex-column w-100">
                  <p class="font-weight-bold">{{ $t('messages.payment_method_chosen') }}</p>
                  <div class="d-flex">
                    <div class="d-flex align-items-center">
                      <div class="d-flex logo-div justify-content-center">
                        <img :src="paymentMethodImage" class="logo" v-if="order.payment_method !== 'transfer' && order.payment_method !== 'paypal' && order.payment_method !== 'creditcard'" />
                        <font-awesome-icon icon="money-check-alt" class="red logo w-100" v-if="order.payment_method === 'transfer'"></font-awesome-icon>
                        <font-awesome-icon :icon="['fab', 'paypal']" class="red logo w-100" v-if="order.payment_method === 'paypal'"></font-awesome-icon>
                        <font-awesome-icon icon="credit-card" class="red logo w-100" v-if="order.payment_method === 'creditcard'"></font-awesome-icon>
                      </div>
                      <div class="pl-2">{{ $t('payments.' + order.payment_method) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column align-items-end w-100">
                <p class="mr-1">
                  <span class="font-weight-bold">{{ $t('messages.total_sub') }}</span>: € {{ subTotalSum | round(2) }}
                </p>
                <p class="mr-1 red">
                  <span class="font-weight-bold">{{ !order.pickup ? $t('messages.shipping_costs') : $t('messages.pickup_costs') }}</span>: € {{ order.shipping ? order.shipping : 0 | round(2) }}
                </p>
                <p class="mr-1 mt-2 red" v-if="order.discount !== null">
                  <span class="font-weight-bold">{{ $t('messages.discount') }}</span>: € {{ discountSum | round(2) }}
                </p>
                <p class="mr-1 mt-2 red" v-if="order.payment_method === 'paypal'">
                  <span class="font-weight-bold">{{ $t('messages.extra_costs') }}</span>: € {{ paypalSum | round(2) }}
                </p>
                <p class="mr-1 mt-2 red" v-if="order.payment_method === 'creditcard'">
                  <span class="font-weight-bold">{{ $t('messages.extra_costs') }}</span>: € {{ creditcardSum | round(2) }}
                </p>
                <p class="mr-1 mt-2">
                  <span class="font-weight-bold">{{ $t('messages.total') }}</span>: € {{ (totalSum + paypalSum + creditcardSum) | round(2) }}
                </p>
              </div>
            </div>
            <div v-if="order.status === 'open'" class="mt-2">
              <div v-if="order.payment_method === 'transfer'" class="mb-2">
                <p class="font-weight-bold">{{ $t('messages.order_transfer') }}</p>
                <p>CrowdFinder</p>
                <p>{{ $t('messages.iban') }}: {{ $t('messages.order_transfer_iban') }}</p>
                <p>{{ $t('messages.bic') }}: {{ $t('messages.order_transfer_bic') }}</p>
              </div>
              <p>{{ $t('messages.order_processing') }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex-responsive">
        <div class="card w-100" v-if="order.pickup">
          <h1 class="card__title">{{ $t('messages.pickup_chosen') }}</h1>
          <div class="card__description border-0">
            <p class="font-weight-bold red">{{ order.pickup.city }}, {{ $t('countries.' + order.pickup.country) }}</p>
            <p class="mt-2 font-weight-bold">{{ $t('pickup.' + order.pickup.info) }}</p>
            <p class="mt-2">{{ $t('messages.pickup_address') }}</p>
            <div v-if="isAdmin" class="mt-2">
              <b-form @submit.prevent="sendDeliveredMail">
                <notification-button :message="$t('messages.delivered_notification_send')"
                                     :notification="notifications.deliveredNotification" field="deliveredNotification"></notification-button>
              </b-form>
            </div>
          </div>
        </div>
        <div class="card w-100" v-if="order.shipping_country">
          <h1 class="card__title">{{ order.shipping_pickup ? $t('messages.address_shipping_pickup') : $t('messages.address_shipping') }}</h1>
          <div class="card__description border-0">
            <div class="d-flex">
              <div class="w-100">
                {{ order.shipping_firstname }} {{ order.shipping_lastname }}<br />
                {{ order.shipping_street }} {{ order.shipping_house_number }}<br />
                {{ order.shipping_postal_code }} {{ order.shipping_city }}<br />
                {{ $t('countries.' + order.shipping_country) }}
                <div v-if="order.phone_number">
                  <br />
                  {{ order.phone_number }}
                </div>
                <div v-if="isAdmin">
                  <br />
                  <a :href="'mailto:'+order.email">{{ order.email }}</a>
                </div>
              </div>
              <div v-if="order.shipping_pickup"><b-img :src="require('@/assets/carrier/' + order.shipping_pickup + '.png')" class="carrier-width" /></div>
            </div>
          </div>
        </div>
        <div class="card w-100" v-if="!order.pickup && !order.shipping_country">
          <h1 class="card__title">{{ $t('messages.delivery') }}</h1>
          <div class="card__description border-0">
            <div class="d-flex">
              <div class="w-100">
                <i>{{ $t('messages.shipping_combine') }}</i>
                <div v-if="order.phone_number">
                  <br />
                  {{ order.phone_number }}
                </div>
                <div v-if="isAdmin">
                  <br />
                  <a :href="'mailto:'+order.email">{{ order.email }}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card w-100 billing-margin-left">
          <h1 class="card__title">{{ $t('messages.address_billing') }}</h1>
          <div class="card__description border-0">
            <p>
              {{ order.billing_firstname }} {{ order.billing_lastname }}<br />
              {{ order.billing_street }} {{ order.billing_house_number }}<br />
              {{ order.billing_postal_code }} {{ order.billing_city }}<br />
              {{ $t('countries.' + order.billing_country) }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="redirect" id="trustedShopsCheckout" style="display: none;">
        <span id="tsCheckoutOrderNr">{{ $route.params.id }}</span>
        <span id="tsCheckoutBuyerEmail">{{ order.email }}</span>
        <span id="tsCheckoutOrderAmount">{{ (totalSum + paypalSum + creditcardSum) | round(2) }}</span>
        <span id="tsCheckoutOrderCurrency">EUR</span>
        <span id="tsCheckoutOrderPaymentType">{{ order.payment_method }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defaultMixin } from '../../mixin/default'
import { notificationMixin } from '../../mixin/notification'

import { orderService } from '../../services/order.service'

import NotificationButton from '../../components/NotificationButton'

export default {
  name: 'order',
  metaInfo: function () {
    return {
      title: this.$i18n.t('messages.order') + ' #' + this.$route.params.id + ' - CrowdFinder',
      meta: [
        {
          'property': 'og:title',
          'content': this.$i18n.t('messages.order') + ' #' + this.$route.params.id,
          'vmid': 'og:title'
        }
      ]
    }
  },
  components: { NotificationButton },
  mixins: [ defaultMixin, notificationMixin ],
  props: {
    redirect: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      fields: [{
        key: 'image',
        label: this.$i18n.t('messages.image'),
        tdClass: 'td-image'
      }, {
        key: 'name',
        label: this.$i18n.t('messages.name')
      }, {
        key: 'amount',
        label: this.$i18n.t('messages.number'),
        tdClass: 'td-info'
      }, {
        key: 'price',
        label: this.$i18n.t('messages.price'),
        tdClass: 'td-info'
      }, {
        key: 'total',
        label: this.$i18n.t('messages.total'),
        tdClass: 'td-info'
      }, {
        key: 'reserved',
        label: this.$i18n.t('messages.reserved'),
        tdClass: 'td-info'
      }, {
        key: 'delivered',
        label: this.$i18n.t('messages.delivered'),
        tdClass: 'td-info'
      }],
      order: null,
      status: null,
      delivered: null,
      statuses: [ 'canceled', 'expired', 'failed', 'open', 'paid' ],
      notifications: {
        deliveredNotification: null,
        saveComment: null
      }
    }
  },
  computed: {
    paymentMethodImage: function () {
      if (this.order.payment_method) {
        return require('../../assets/payment/' + this.order.payment_method + '.png')
      }
      return null
    },
    subTotalSum: function () {
      return this.order.products.map(product => product.amount * product.price).reduce((a, b) => a + b, 0)
    },
    discountSum: function () {
      return this.order.discount
    },
    totalSum: function () {
      return this.subTotalSum + (this.order.shipping ? this.order.shipping : 0) - (this.discountSum ? this.discountSum : 0)
    },
    paypalSum: function () {
      return this.order.payment_method === 'paypal' ? this.totalSum * 0.05 : 0
    },
    creditcardSum: function () {
      return this.order.payment_method === 'creditcard' ? this.totalSum * 0.02 : 0
    }
  },
  methods: {
    getDeliveredMessage: function () {
      if (this.order) {
        if (this.order.pickup) {
          return this.$i18n.t('messages.pickup_able')
        } else if (this.order.shipping_pickup || this.order.shipping) {
          return this.$i18n.t('messages.shipped')
        } else {
          return this.$i18n.t('messages.delivered')
        }
      } else {
        return this.$i18n.t('messages.delivered')
      }
    },
    formatDate: function (date) {
      let translatedDate = this.translateDate(date).split(' ')
      return translatedDate[0] + ' ' + translatedDate[1] + ' ' + translatedDate[2]
    },
    fetchOrder: function (id) {
      let loader = this.$loading.show()
      orderService.get(id)
        .then((order) => {
          this.order = order
          this.status = order.status
          if (this.redirect) {
            if (this.order.status === 'open') {
              this.$store.commit('success', 'messages.order_thanks_open')
              this.$store.commit('clearCart')
            } else if (this.order.status === 'paid') {
              this.$store.commit('success', 'messages.order_thanks_paid')
              this.$store.commit('clearCart')
            } else {
              this.$store.commit('error', 'errors.order_thanks')
            }
          }
          this.fields[this.fields.length - 1].label = this.getDeliveredMessage()
          loader.hide()
        }, () => {
          this.$store.commit('error', 'errors.order_view')
          loader.hide()
        })
    },
    captureOrder: function (id) {
      let loader = this.$loading.show()
      orderService.captureOrder(parseInt(this.$route.params.id), id)
        .then(() => {
          loader.hide()
          this.fetchOrder(this.$route.params.id)
        }, () => {
          loader.hide()
          this.fetchOrder(this.$route.params.id)
        })
    },
    changeDelivered: function (item) {
      orderService.changeDelivered(item.pop_id, item.delivered)
        .then(() => {
          this.$store.commit('success', 'success.delivered_changed')
        }, () => {
          this.$store.commit('error', 'errors.delivered_changed')
        })
    },
    sendDeliveredMail: function () {
      orderService.sendDeliveredMail(parseInt(this.$route.params.id), this.order.pickup.person[0].id)
        .then(() => {
          this.$store.commit('success', 'success.delivered_notification')
          this.sendNotification('deliveredNotification', 'success')
        }, () => {
          this.$store.commit('error', 'errors.delivered_notification')
          this.sendNotification('deliveredNotification', 'error')
        })
    },
    saveComment: function () {
      orderService.saveComment(parseInt(this.$route.params.id), this.order.comments)
        .then(() => {
          this.$store.commit('success', 'success.comments_notification')
          this.sendNotification('saveComment', 'success')
        }, () => {
          this.$store.commit('error', 'errors.comments_notification')
          this.sendNotification('saveComment', 'error')
        })
    },
    navigateToProduct: function (product) {
      this.$router.push('/product/' + this.slugText(product.id + '/' + product.name))
    }
  },
  watch: {
    status: function () {
      if (this.status !== this.order.status) {
        orderService.changeStatus(this.$route.params.id, this.status)
          .then(() => {
            this.order.status = this.status
            this.fetchOrder(this.$route.params.id)
            this.$store.commit('success', 'success.status_changed')
          }, () => {
            this.$store.commit('error', 'errors.status_changed')
          })
      }
    }
  },
  created: function () {
    if (!this.$route.params.id) {
      this.$router.push('/orders')
    }
    if (this.$route.query.token && this.$route.query.PayerID && this.redirect) {
      this.captureOrder(this.$route.query.token)
    } else {
      this.fetchOrder(this.$route.params.id)
    }

    this.$root.$on('resetNotification', (notification) => {
      this.resetNotification(notification)
    })
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .table >>> .td-image img {
    max-height: 100px !important;
  }

  .carrier-width {
    width: 150px;
  }

  .d-flex-responsive-order {
    display: block;
  }

  .details-margin {
    margin-bottom: 10px;
  }

  @media screen and (min-width: 768px) {
    .table >>> .td-image {
      width: 150px !important;
      text-align: center;
    }

    .table >>> .td-info {
      width: 150px !important;
    }

    .d-flex-responsive-order {
      display: -webkit-flex; /* Safari */
      display: flex;
    }

    .d-flex-responsive-order .width-50 {
      width: 50%;
    }

    .details-margin {
      margin-bottom: 0px;
    }
  }

  @media screen and (min-width: 901px) {
    .billing-margin-left {
      margin-left: 15px;
    }
  }

  .table >>> td {
    vertical-align: middle !important;
  }

  .logo-div {
    width: 50px;
  }

  .logo {
    height: 35px;
  }
</style>



// WEBPACK FOOTER //
// src/pages/product/Order.vue